import React, { useEffect, useState } from 'react';
import { 
  Search, 
  FileText, 
  Download, 
  ChevronLeft, 
  ChevronRight, 
  Package,
  AlertCircle,
  Filter
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/Auth';

const PrintStockReport = () => {
  const [searchParams] = useSearchParams();
  const branch = searchParams.get("branch");
  const date = searchParams.get("date");
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [creditList, setCreditList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [count, setCount] = useState(0);

  const fetchCreditList = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/report/stock?page=${current_page}&pageSize=${page_size}&search=${search_query}&branch=${branch}&date=${date}`,
        "GET"
      );
      setCreditList(data?.results || []);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setCount(data?.count || 0);
      setTotalPages(Math.ceil((data?.count || 0) / page_size));
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterCreditList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 2;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) pageNumbers.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-600 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchCreditList(pageSize, search, currentPage);
  }, [search, currentPage, pageSize]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <span>Inventory</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Stock Report</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileText className="text-blue-600 font-bold" size={32} />
                Stock Report
              </h1>
              <p className="text-gray-600 mt-1">Detailed inventory stock levels and valuations</p>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Filters & Actions */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="bg-gray-100 p-2 rounded-lg">
                    <Download size={18} className="text-gray-600" />
                  </div>
                  <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border-r border-gray-200 transition-colors">
                      CSV
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                      PDF
                    </button>
                  </div>
                </div>

                <div className="h-8 border-l border-gray-200 mx-2 hidden md:block"></div>

                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">Show:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search stock..."
                    onChange={(e) => filterCreditList(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                  <Filter size={18} className="text-gray-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Table container */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Item Name
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider text-center">
                    Quantity
                  </th>
                  <th className="py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">
                    Unit Cost (GH₵)
                  </th>
                  <th className="py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">
                    Total Cost (GH₵)
                  </th>
                  <th className="py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">
                    Stock Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  Array(pageSize).fill(0).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {Array(6).fill(0).map((_, j) => (
                        <td key={j} className="py-4 px-6">
                          <div className="h-4 bg-gray-100 rounded"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : creditList?.length > 0 ? (
                  creditList.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item?.drug || "N/A"}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.branch || "N/A"}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900 text-center font-semibold">
                        {item?.quantity?.toLocaleString() || 0}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 text-right">
                        {item?.unit_cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-4 px-6 text-sm font-bold text-gray-900 text-right">
                        {item?.total_cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 text-right">
                        {item?.stock_date || "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-20 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="bg-gray-50 p-4 rounded-full mb-4">
                          <Package size={40} className="text-gray-300" />
                        </div>
                        <p className="text-gray-500 font-medium">No stock data available</p>
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or check back later</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / Pagination */}
          {!loading && creditList?.length > 0 && (
            <div className="p-6 border-t border-gray-200 bg-white">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <p className="text-sm text-gray-500">
                  Showing <span className="font-semibold text-gray-900">{((currentPage - 1) * pageSize) + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(currentPage * pageSize, count)}</span> of <span className="font-semibold text-gray-900">{count}</span> entries
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={!prevPage}
                    className={`p-2 rounded-lg transition-all ${prevPage
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>
                  <button
                    onClick={handleNextPage}
                    disabled={!nextPage}
                    className={`p-2 rounded-lg transition-all ${nextPage
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintStockReport;